import { Injectable, inject } from '@angular/core';
import { ToastController, AppToastOptions } from 'src/app/core/controllers/toast/toast-controller';

/**
 * Brand-aligned toast notifications. Thin facade around the existing
 * {@link ToastController} so feature code imports a single ui-kit symbol.
 *
 * Every variant ships with brand-consistent defaults (color, icon, duration,
 * position). Callers override any option per-call via the second argument.
 *
 * @example
 * constructor() {
 *   this.toast = inject(ToastService);
 * }
 *
 * await this.toast.success('Profile saved');
 * await this.toast.error('Failed to save', { duration: 4000 });
 *
 * @feature shared
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly controller = inject(ToastController);

  /** Success toast (green + checkmark). */
  success(message: string, options?: Partial<AppToastOptions>): Promise<HTMLIonToastElement> {
    return this.controller.success(message, options);
  }

  /** Error toast (red + alert). */
  error(message: string, options?: Partial<AppToastOptions>): Promise<HTMLIonToastElement> {
    return this.controller.error(message, options);
  }

  /** Warning toast (orange + warning icon). */
  warning(message: string, options?: Partial<AppToastOptions>): Promise<HTMLIonToastElement> {
    return this.controller.warning(message, options);
  }

  /** Info toast (primary + information icon). */
  info(message: string, options?: Partial<AppToastOptions>): Promise<HTMLIonToastElement> {
    return this.controller.info(message, options);
  }

  /** Loading toast (does not auto-dismiss). Call `dismiss()` when done. */
  loading(message: string, options?: Partial<AppToastOptions>): Promise<HTMLIonToastElement> {
    return this.controller.loading(message, options);
  }

  /** Dismiss the currently active toast. */
  dismiss(data?: unknown, role?: string): Promise<boolean> {
    return this.controller.dismiss(data, role);
  }

  /** Escape hatch: present a raw toast with any Ionic options. */
  present(options: AppToastOptions): Promise<HTMLIonToastElement> {
    return this.controller.present(options);
  }
}
